// =============================================
// Admin Settings Page
// Kipikid - מערכת נוכחות ילדים בגנים
// חיבור ל-API - הגדרות היררכיות
// =============================================

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Bell,
  Clock,
  Globe,
  Save,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Layers,
  RotateCcw,
} from 'lucide-react';
import { adminApi, type EffectiveParameterDto } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import FormLabel from '../../components/common/FormLabel';
import HelperText from '../../components/common/HelperText';
import { adminSettingsHelpTexts } from '../../constants/helpTexts';

import { adminColors as colors } from '../../styles/colors';

// Parameter keys mapping
const PARAM_KEYS = {
  FIRST_ALERT_DELAY: 'FirstAlertDelayMinutes',
  SECOND_ALERT_DELAY: 'SecondAlertDelayMinutes',
  MAX_ALERT_DELAY: 'MaxAlertDelayMinutes',
  DEFAULT_OPEN_TIME: 'DefaultOpenTime',
  DEFAULT_CLOSE_TIME: 'DefaultCloseTime',
  DISPLAY_SWITCH_TIME: 'DefaultDisplaySwitchTime',
  LATEST_ARRIVAL_TIME: 'LatestArrivalTime',
  REFRESH_INTERVAL: 'RefreshIntervalSeconds',
  DEFAULT_LANGUAGE: 'DefaultLanguage',
};

export default function AdminSettings() {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);

  // זיהוי רמת המשתמש
  const isSysAdmin = location.pathname.startsWith('/sysadmin');
  const entityType = isSysAdmin ? 0 : 1; // 0=System, 1=Network
  const orgPublicId = !isSysAdmin ? user?.organizationPublicId : undefined;

  const [parameters, setParameters] = useState<EffectiveParameterDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Editable values - mapped from parameters
  const [firstAlertDelay, setFirstAlertDelay] = useState('5');
  const [secondAlertDelay, setSecondAlertDelay] = useState('15');
  const [maxAlertDelay, setMaxAlertDelay] = useState('90');
  const [defaultOpenTime, setDefaultOpenTime] = useState('07:00');
  const [defaultCloseTime, setDefaultCloseTime] = useState('16:00');
  const [displaySwitchTime, setDisplaySwitchTime] = useState('09:00');
  const [latestArrivalTime, setLatestArrivalTime] = useState('09:30');
  const [refreshInterval, setRefreshInterval] = useState('120');
  const [language, setLanguage] = useState('he');

  // Track which params are overridden at this level
  const [overriddenKeys, setOverriddenKeys] = useState<Set<string>>(new Set());
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const loadParameters = async () => {
      setLoading(true);
      try {
        const result = await adminApi.getEffectiveParameters(entityType, orgPublicId);
        if (result.success && result.data) {
          setParameters(result.data);
          const overridden = new Set<string>();
          for (const param of result.data) {
            if (param.isOverridden) overridden.add(param.paramKey);
            switch (param.paramKey) {
              case PARAM_KEYS.FIRST_ALERT_DELAY: setFirstAlertDelay(param.paramValue); break;
              case PARAM_KEYS.SECOND_ALERT_DELAY: setSecondAlertDelay(param.paramValue); break;
              case PARAM_KEYS.MAX_ALERT_DELAY: setMaxAlertDelay(param.paramValue); break;
              case PARAM_KEYS.DEFAULT_OPEN_TIME: setDefaultOpenTime(param.paramValue); break;
              case PARAM_KEYS.DEFAULT_CLOSE_TIME: setDefaultCloseTime(param.paramValue); break;
              case PARAM_KEYS.DISPLAY_SWITCH_TIME: setDisplaySwitchTime(param.paramValue); break;
              case PARAM_KEYS.LATEST_ARRIVAL_TIME: setLatestArrivalTime(param.paramValue); break;
              case PARAM_KEYS.REFRESH_INTERVAL: setRefreshInterval(param.paramValue); break;
              case PARAM_KEYS.DEFAULT_LANGUAGE: setLanguage(param.paramValue); break;
            }
          }
          setOverriddenKeys(overridden);
        }
      } catch (err) {
        console.error('Failed to load parameters:', err);
        setError('שגיאה בטעינת הגדרות');
      } finally {
        setLoading(false);
      }
    };
    loadParameters();
  }, [entityType, orgPublicId, refreshKey]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);

    try {
      const updates = [
        { paramKey: PARAM_KEYS.FIRST_ALERT_DELAY, paramValue: firstAlertDelay },
        { paramKey: PARAM_KEYS.SECOND_ALERT_DELAY, paramValue: secondAlertDelay },
        { paramKey: PARAM_KEYS.MAX_ALERT_DELAY, paramValue: maxAlertDelay },
        { paramKey: PARAM_KEYS.DEFAULT_OPEN_TIME, paramValue: defaultOpenTime },
        { paramKey: PARAM_KEYS.DEFAULT_CLOSE_TIME, paramValue: defaultCloseTime },
        { paramKey: PARAM_KEYS.DISPLAY_SWITCH_TIME, paramValue: displaySwitchTime },
        { paramKey: PARAM_KEYS.LATEST_ARRIVAL_TIME, paramValue: latestArrivalTime },
        { paramKey: PARAM_KEYS.REFRESH_INTERVAL, paramValue: refreshInterval },
        { paramKey: PARAM_KEYS.DEFAULT_LANGUAGE, paramValue: language },
      ];

      const result = await adminApi.updateParameters(
        entityType,
        null,
        updates,
        orgPublicId
      );
      if (result.success) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
        // רענון כדי לקבל מצב עדכני של isOverridden
        setRefreshKey(k => k + 1);
      } else {
        setError(result.message || 'שגיאה בשמירת הגדרות');
      }
    } catch (err) {
      console.error('Failed to save parameters:', err);
      setError('שגיאה בשמירת הגדרות');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = async (paramKey: string) => {
    if (!orgPublicId) return;
    try {
      await adminApi.deleteParameterOverride(entityType, orgPublicId, paramKey);
      setRefreshKey(k => k + 1);
    } catch (err) {
      console.error('Failed to reset parameter:', err);
    }
  };

  const getParamMeta = (paramKey: string) => {
    const param = parameters.find((p) => p.paramKey === paramKey);
    return param;
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    fontSize: '15px',
    border: `2px solid ${colors.border}`,
    borderRadius: '10px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    direction: 'rtl' as const,
  };

  // Badge showing "default" or "overridden"
  const ParamBadge = ({ paramKey }: { paramKey: string }) => {
    if (isSysAdmin) return null; // SysAdmin תמיד ברמת System — אין ירושה
    const meta = getParamMeta(paramKey);
    if (!meta) return null;

    if (meta.isOverridden) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span style={{
            fontSize: '11px', color: colors.primary, backgroundColor: colors.primaryLight,
            padding: '2px 8px', borderRadius: '6px', fontWeight: '500',
          }}>
            מותאם
          </span>
          <button
            onClick={() => handleResetToDefault(paramKey)}
            title="אפס לברירת מחדל"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '3px',
              fontSize: '11px', color: colors.textLight, background: 'none',
              border: 'none', cursor: 'pointer', padding: '2px 4px',
            }}
          >
            <RotateCcw size={12} />
            אפס
          </button>
        </span>
      );
    }

    return (
      <span style={{
        fontSize: '11px', color: colors.textLight, backgroundColor: colors.background,
        padding: '2px 8px', borderRadius: '6px', fontWeight: '400',
      }}>
        ברירת מחדל
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px', color: colors.textLight }}>
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', marginLeft: '8px' }} />
        טוען הגדרות...
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '600px' }}>
      {/* Header */}
      <div>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: colors.text, marginBottom: '4px' }}>
          {isSysAdmin ? 'הגדרות מערכת (ברירת מחדל)' : 'הגדרות רשת'}
        </h1>
        <p style={{ fontSize: '14px', color: colors.textLight }}>
          {isSysAdmin
            ? 'ערכים אלו חלים על כל המערכת. רשתות וגנים יכולים לדרוס אותם.'
            : `הגדרות עבור ${user?.organizationName || 'הרשת שלך'}. ערכי "ברירת מחדל" ירשים מהגדרות המערכת.`
          }
        </p>
      </div>

      {/* Hierarchy Info */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        padding: '12px 14px',
        backgroundColor: colors.primaryLight,
        borderRadius: '12px',
      }}>
        <Layers size={18} color={colors.primary} style={{ flexShrink: 0, marginTop: '2px' }} />
        <div>
          <p style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '4px' }}>
            הגדרות היררכיות
          </p>
          <p style={{ fontSize: '12px', color: '#4338ca', lineHeight: '1.6', margin: 0 }}>
            {isSysAdmin
              ? 'הגדרות אלו חלות על כל המערכת. ניתן לדרוס אותן ברמת הרשת/ארגון או ברמת הגן הבודד.'
              : 'ערכים ב"ברירת מחדל" מגיעים מהגדרות המערכת. שינוי ערך יצור התאמה ברמת הרשת שלך.'
            }
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 14px',
          backgroundColor: '#fef2f2',
          borderRadius: '8px',
        }}>
          <AlertCircle size={16} color={colors.error} />
          <span style={{ fontSize: '13px', color: colors.error }}>{error}</span>
        </div>
      )}

      {/* Notification Settings */}
      <SettingsCard
        title="הגדרות התראות"
        icon={Bell}
        iconColor={colors.primary}
        iconBgColor={colors.primaryLight}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <FormLabel text="זמן המתנה להתראה ראשונה (דקות)" helpText={adminSettingsHelpTexts.firstAlertDelay.tooltip} style={{ color: colors.text, marginBottom: 0 }} />
              <ParamBadge paramKey={PARAM_KEYS.FIRST_ALERT_DELAY} />
            </div>
            <input
              type="number"
              value={firstAlertDelay}
              onChange={(e) => setFirstAlertDelay(e.target.value)}
              min="1"
              max="60"
              style={{ ...inputStyle, width: '120px', direction: 'ltr', textAlign: 'center' }}
            />
            <HelperText text={adminSettingsHelpTexts.firstAlertDelay.helper!} />
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <FormLabel text="זמן המתנה להתראה שנייה (דקות)" helpText={adminSettingsHelpTexts.secondAlertDelay.tooltip} style={{ color: colors.text, marginBottom: 0 }} />
              <ParamBadge paramKey={PARAM_KEYS.SECOND_ALERT_DELAY} />
            </div>
            <input
              type="number"
              value={secondAlertDelay}
              onChange={(e) => setSecondAlertDelay(e.target.value)}
              min="1"
              max="60"
              style={{ ...inputStyle, width: '120px', direction: 'ltr', textAlign: 'center' }}
            />
            <HelperText text={adminSettingsHelpTexts.secondAlertDelay.helper!} />
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <FormLabel text="זמן מקסימלי להתראה (דקות)" helpText={adminSettingsHelpTexts.maxAlertDelay.tooltip} style={{ color: colors.text, marginBottom: 0 }} />
              <ParamBadge paramKey={PARAM_KEYS.MAX_ALERT_DELAY} />
            </div>
            <input
              type="number"
              value={maxAlertDelay}
              onChange={(e) => setMaxAlertDelay(e.target.value)}
              min="1"
              max="240"
              style={{ ...inputStyle, width: '120px', direction: 'ltr', textAlign: 'center' }}
            />
            <HelperText text={adminSettingsHelpTexts.maxAlertDelay.helper!} />
          </div>
        </div>
      </SettingsCard>

      {/* Time Settings */}
      <SettingsCard
        title="הגדרות זמן"
        icon={Clock}
        iconColor={colors.secondary}
        iconBgColor={colors.secondaryLight}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <FormLabel text="שעת פתיחה ברירת מחדל" helpText={adminSettingsHelpTexts.defaultOpenTime.tooltip} style={{ color: colors.text, marginBottom: 0 }} />
                <ParamBadge paramKey={PARAM_KEYS.DEFAULT_OPEN_TIME} />
              </div>
              <input
                type="time"
                value={defaultOpenTime}
                onChange={(e) => setDefaultOpenTime(e.target.value)}
                style={inputStyle}
              />
              <HelperText text={adminSettingsHelpTexts.defaultOpenTime.helper!} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <FormLabel text="שעת סגירה ברירת מחדל" helpText={adminSettingsHelpTexts.defaultCloseTime.tooltip} style={{ color: colors.text, marginBottom: 0 }} />
                <ParamBadge paramKey={PARAM_KEYS.DEFAULT_CLOSE_TIME} />
              </div>
              <input
                type="time"
                value={defaultCloseTime}
                onChange={(e) => setDefaultCloseTime(e.target.value)}
                style={inputStyle}
              />
              <HelperText text={adminSettingsHelpTexts.defaultCloseTime.helper!} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <FormLabel text="שעת החלפת תצוגה" helpText={adminSettingsHelpTexts.displaySwitchTime.tooltip} style={{ color: colors.text, marginBottom: 0 }} />
                <ParamBadge paramKey={PARAM_KEYS.DISPLAY_SWITCH_TIME} />
              </div>
              <input
                type="time"
                value={displaySwitchTime}
                onChange={(e) => setDisplaySwitchTime(e.target.value)}
                style={{ ...inputStyle, width: '150px' }}
              />
              <HelperText text={adminSettingsHelpTexts.displaySwitchTime.helper!} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <FormLabel text="שעת הגעה אחרונה" helpText={adminSettingsHelpTexts.latestArrivalTime.tooltip} style={{ color: colors.text, marginBottom: 0 }} />
                <ParamBadge paramKey={PARAM_KEYS.LATEST_ARRIVAL_TIME} />
              </div>
              <input
                type="time"
                value={latestArrivalTime}
                onChange={(e) => setLatestArrivalTime(e.target.value)}
                style={{ ...inputStyle, width: '150px' }}
              />
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <FormLabel text="תדירות רענון טאבלט (שניות)" helpText={adminSettingsHelpTexts.refreshInterval.tooltip} style={{ color: colors.text, marginBottom: 0 }} />
              <ParamBadge paramKey={PARAM_KEYS.REFRESH_INTERVAL} />
            </div>
            <input
              type="number"
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(e.target.value)}
              min="30"
              max="600"
              step="10"
              style={{ ...inputStyle, width: '120px', direction: 'ltr', textAlign: 'center' }}
            />
            <HelperText text={adminSettingsHelpTexts.refreshInterval.helper!} />
          </div>
        </div>
      </SettingsCard>

      {/* Language Settings */}
      <SettingsCard
        title="שפה"
        icon={Globe}
        iconColor={colors.success}
        iconBgColor={colors.successLight}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <FormLabel text="שפת ברירת מחדל" helpText={adminSettingsHelpTexts.language.tooltip} style={{ color: colors.text, marginBottom: 0 }} />
            <ParamBadge paramKey={PARAM_KEYS.DEFAULT_LANGUAGE} />
          </div>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            style={{ ...inputStyle, width: '200px', cursor: 'pointer' }}
          >
            <option value="he">עברית</option>
            <option value="ar">ערבית</option>
            <option value="en">English</option>
          </select>
        </div>
      </SettingsCard>

      {/* Save Button */}
      <motion.button
        onClick={handleSave}
        disabled={saving}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '14px 24px',
          borderRadius: '12px',
          background: saved
            ? colors.success
            : `linear-gradient(135deg, ${colors.primary} 0%, #4f46e5 100%)`,
          color: 'white',
          fontSize: '15px',
          fontWeight: '600',
          border: 'none',
          cursor: saving ? 'not-allowed' : 'pointer',
          width: 'fit-content',
          opacity: saving ? 0.7 : 1,
        }}
        whileHover={{ scale: saving ? 1 : 1.02 }}
        whileTap={{ scale: saving ? 1 : 0.98 }}
      >
        {saving ? (
          <>
            <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
            שומר...
          </>
        ) : saved ? (
          <>
            <CheckCircle2 size={18} />
            נשמר בהצלחה
          </>
        ) : (
          <>
            <Save size={18} />
            שמור הגדרות
          </>
        )}
      </motion.button>
    </div>
  );
}

function SettingsCard({
  title,
  icon: Icon,
  iconColor,
  iconBgColor,
  children,
}: {
  title: string;
  icon: React.ElementType;
  iconColor: string;
  iconBgColor: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          backgroundColor: iconBgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Icon size={20} color={iconColor} />
        </div>
        <h2 style={{ fontSize: '16px', fontWeight: '600', color: colors.text }}>
          {title}
        </h2>
      </div>
      {children}
    </div>
  );
}
